import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Text, Button, InnerTab } from "@netapp/bxp-design-system-react";
import "./detail.scss";
import SubscribeModal from "../../../Components/Subscribe_modal/Subscribe";
import UnsubscribeModal from "../../../Components/Subscribe_modal/UnsubscribeModal"; // Import the UnsubscribeModal component
import DOMPurify from 'dompurify';

const sanitizeValue = (value) => {
  if (typeof value === "string") {
    return value.replace(/[\r\n]+/g, " ");
  }
  return value;
};
// Utility function to format ISO date to "MM/DD/YYYY"
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const dateObj = new Date(dateStr);
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const day = dateObj.getDate().toString().padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${month}/${day}/${year}`;
};

const Header = ({
  advisory,
  advisoryData,
  cve,
  currentTab,
  setCurrentTab,
  advisoryId,
  notification,
  setNotification,
}) => {
  const { t } = useTranslation();
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash === "#unsubscribe") {
        setUnsubscribeModalOpen(true);
      } else {
        setUnsubscribeModalOpen(false);
      }
    };

    // Check the initial hash value
    handleHashChange();

    // Add event listener for hash changes
    window.addEventListener("hashchange", handleHashChange);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  // Fallback values while advisoryData is not yet loaded
  const advisoryIdDisplay =
    advisoryData?.ntap_advisory_id || advisoryId || "Loading...";
  const lastUpdated = advisoryData?.updated_date
    ? formatDate(advisoryData.updated_date)
    : "Loading...";
  const status = advisoryData?.kb_status || "Loading...";
  const cveText =
    advisoryData?.kb_cve && advisoryData.kb_cve.length > 0
      ? advisoryData.kb_cve.join(", ")
      : cve;

  // Get the latest version from kb_rev_history
  const latestVersion = advisoryData?.kb_rev_history
    ? advisoryData.kb_rev_history[advisoryData.kb_rev_history.length - 1]
        .version
    : "Loading...";

  // Use advisoryData.kb_title if available, otherwise fallback to advisory prop
  const advisoryTitle = advisoryData?.kb_title || advisory;

  const convertToCSV = (data) => {
  console.log(data);
  const csvRows = [];
  const headers = [
    t("csv_advisoryId.other"),
    t("csv_title.other"),
    t("csv_product.other"),
    t("csv_productStatus.other"),
    t("csv_advisoryStatus.other"),
    t("csv_cves.other"),
    t("csv_description.other"),
    t("csv_comment.other"),
    t("csv_version.other"),
    t("csv_date.other"),
    t("csv_year.other"),
    t("csv_fixLinks.other"),
    t("csv_workaround.other"),
    t("csv_burt.other"),
    t("csv_jira.other"),
  ];
  csvRows.push(headers.join(","));

  data.forEach((row) => {
    const values = [
      `"${row.id}"`,
      `"${sanitizeValue(row.title)}"`,
      `"${sanitizeValue(row.product)}"`,
      `"${row.product_status}"`,
      `"${row.kb_status}"`,
      `"${row.score}"`,
      `"${sanitizeValue(row.description)}"`,
      `"${sanitizeValue(row.comment)}"`,
      `"${row.version}"`,
      `"${row.date}"`,
      `"${sanitizeValue(row.year)}"`,
      `"${sanitizeValue(row.fix_links ? row.fix_links : "")}"`,
      `"${sanitizeValue(row.kb_workarounds)}"`,
      `"${row.burt}"`,
      `"${row.jira}"`,
    ];
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
};

const downloadCSV = (csv, filename) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = DOMPurify.sanitize(url);
  a.setAttribute("download", filename);
  a.click();
};

const handleExportCSV = async () => {
  try {
    let availableAffectedProducts = [];
    let availableInvestigatingProducts = [];
    let availableUnaffectedProducts = []; // NEW
  
    advisoryData.kb_affected_list?.forEach((product) => { 
      availableAffectedProducts.push(product);
    });  
  
    advisoryData.kb_investigating_list?.forEach((product) => {
      availableInvestigatingProducts.push(product);
    });

    // Derive "Not Affected" products:
    // products that appear in kb_internal_notes but NOT in kb_affected_list
    const affectedSet = new Set(availableAffectedProducts);
    advisoryData.kb_internal_notes?.forEach((note) => {
      const productName = note.product;
      if (productName && !affectedSet.has(productName) && !availableInvestigatingProducts.includes(productName)) {
        availableUnaffectedProducts.push(productName);
      }
    });
    // Optionally de‑dupe
    availableUnaffectedProducts = [...new Set(availableUnaffectedProducts)];

  
    advisoryData.kb_affected_list = availableAffectedProducts;
    advisoryData.kb_investigating_list = availableInvestigatingProducts;
  
  function generateProductRows(item, productList, status) {
    if (!productList?.length) return [];
    return productList.map((fix) => ({
      title: item.kb_title,
      score: item.kb_scoring_calc?.map((calc) => `${calc.cve_id} ${calc.range} ${calc.score}`).join(", "),
      cve_id: item.kb_cve,
      published_date: formatDate(item.published_date),
      updated_date: formatDate(item.updated_date),
      id: item.ntap_advisory_id,
      description: item.kb_summary,
      kb_status: item.kb_status,
      kb_workarounds: item.kb_workarounds,
      comment:
        item.kb_rev_history.length > 0
          ? item.kb_rev_history[item.kb_rev_history.length - 1].comment
          : "",
      version:
        item.kb_rev_history.length > 0
          ? item.kb_rev_history[item.kb_rev_history.length - 1].version
          : "",
      date:
        item.kb_rev_history.length > 0
          ? item.kb_rev_history[item.kb_rev_history.length - 1].date
          : "",
      year:
        item.kb_rev_history.length > 0
          ? item.kb_rev_history[item.kb_rev_history.length - 1].date.substring(0, 4)
          : "",
      product: fix,
      product_status: status,
      fix_links: item.kb_fixes?.find((product) => product.product === fix)?.fixes?.map((f) => f?.link).join("\n"),
      burt: item.kb_internal_notes.find((note) => note.product === fix)?.burt,        
      jira: item.kb_internal_notes.find((note) => note.product === fix)?.jira,
    }));
  }
  
    const formattedData = [
      ...generateProductRows(
        advisoryData,
        advisoryData.kb_affected_list,
        "Affected"
      ),
      ...generateProductRows(
        advisoryData,
        advisoryData.kb_investigating_list,
        "Investigating"
      ),
      // Derived "Not Affected" products from kb_internal_notes
      ...generateProductRows(
        advisoryData,
        availableUnaffectedProducts,
        "Not Affected",
      ),
    ];
    
  console.log(formattedData);
  const csvData = convertToCSV(formattedData);
  downloadCSV(csvData, `${advisoryIdDisplay}_advisory.csv`);
  } catch (error) {
    console.error("Error exporting data:", error);
  }
};



  return (
    <div>
      <Text className="title">{advisoryTitle}</Text>
      <div className="details-section">
        <div className="details-header-left">
          <div className="details-info">
            <Text className="details-label">
              {t("advisory_single_header_advisoryId.other")}
            </Text>
            <Text className="details-value">{advisoryIdDisplay}</Text>
          </div>
          <div className="details-info">
            <Text className="details-label">
              {t("advisory_single_header_version.other")}
            </Text>
            <Text className="details-value">{latestVersion}</Text>
          </div>
          <div className="details-info">
            <Text className="details-label">
              {t("advisory_single_header_lastUpdated.other")}
            </Text>
            <Text className="details-value">{lastUpdated}</Text>
          </div>
          <div className="details-info">
            <Text className="details-label">
              {t("advisory_single_header_status.other")}
            </Text>
            <Text className="details-value">{status}</Text>
          </div>
          <div className="details-info">
            <Text className="details-label">
              {t("advisory_single_header_cves.other")}
            </Text>
            <Text className="details-value">{cveText}</Text>
          </div>
        </div>
        <div className="details-header-right">
          <div className="details-info-button">
            <Button
              className="button"
              color={"secondary"}
              onClick={() => setSubscribeModalOpen(true)}
            >
              {t("advisory_subscribeEmailUpdates.other")}
            </Button>
            <Button className="button" color={"secondary"} onClick={handleExportCSV}>
              {t("secPortal_export.other")}
            </Button>
          </div>
          <SubscribeModal
            advisoryId={advisory}
            open={subscribeModalOpen}
            setOpen={setSubscribeModalOpen}
            notification={notification}
            setNotification={setNotification}
          />
          <UnsubscribeModal
            advisoryId={advisoryIdDisplay}
            open={unsubscribeModalOpen}
            setOpen={setUnsubscribeModalOpen}
            headerText={
              advisoryIdDisplay === "ntap_all"
                ? t("advisory_unsubscribeAllUpdates.other")
                : t("advisory_unsubscribeFromAdvisory.other", { id: advisoryIdDisplay })
            }
            notification={notification}
            setNotification={setNotification}
          />
        </div>
      </div>
      <InnerTab className="inner-tab-header">
        <InnerTab.Button
          className="tab-button"
          isActive={currentTab === 1}
          onClick={() => setCurrentTab(1)}
        >
          {t("advisory_single_section_overview.other")}
        </InnerTab.Button>
        <InnerTab.Button
          className="tab-button"
          isActive={currentTab === 2}
          onClick={() => setCurrentTab(2)}
        >
          {t("advisory_single_section_affectedProducts.other")}
        </InnerTab.Button>
        <InnerTab.Button
          className="tab-button"
          isActive={currentTab === 3}
          onClick={() => setCurrentTab(3)}
        >
          {t("advisory_single_section_remediation.other")}
        </InnerTab.Button>
        <InnerTab.Button
          className="tab-button"
          isActive={currentTab === 4}
          onClick={() => setCurrentTab(4)}
        >
          {t("advisory_single_section_revisionHistory.other")}
        </InnerTab.Button>
      </InnerTab>
    </div>
  );
};

export default Header;
